/**
 * Home layout W, "App first" (v2, after the CTO + investor reviews). The phone app is the main ask:
 * Isaac's "Stop cutting" spot plays in the hero phone next to the store badges and a QR code. Then
 * three phones playing REAL recordings of the app, long videos on the web (the sheet of a real film +
 * a link to the same film in the real editor + the real uploader), the AI section (your AI picks,
 * you stay in charge), a six-row "tick what you'd use, press Play" list, pricing, and the closing
 * line the investor liked. Dark, one cherry accent.
 */
import React from "react";
import { HOME3_CSS } from "./home3Css";
import { AiSection, ClipSheet, PhoneClip, PhoneShot, PhoneVideo, PlanCards, StoreBadges, UploadCard, scrollToId, useHomeFonts } from "./kit";
import { GetAppBar, H3Footer, H3Nav, useHomeActions } from "./chrome";
import { FEATURES_CSS, FeatureList } from "./features";
import { BADGES, LANGS, NAV, PLANS, REAL_EDITOR_URL, SHEET, W } from "../../constants/homeLayoutsText";
import UploadBlock from "../../simple/UploadBlock";
import { UPLOAD_HERE_LABEL } from "../../constants/simpleHomeText";

const TOUR_IDS = ["search", "supercut", "realpeople", "fixword", "languages", "premiere"];

const LayoutWPage: React.FC = () => {
  useHomeFonts();
  const act = useHomeActions();
  return (
    <div className="h3 dark h3-w">
      <style>{HOME3_CSS + FEATURES_CSS + W_CSS}</style>
      <H3Nav
        links={[
          { id: "w-phone", label: NAV.phone },
          { id: "w-web", label: NAV.web },
          { id: "w-ai", label: NAV.ai },
          { id: "w-tour", label: NAV.how },
          { id: "w-pricing", label: NAV.pricing },
        ]}
      />

      <section className="w-hero">
        <div className="wrap w-hero-grid">
          <div>
            <span className="eyebrow">{W.eyebrow}</span>
            <h1>
              {W.title1}
              <br />
              <span className="rw">{W.title2}</span>
            </h1>
            <p className="lede w-sub">{W.sub}</p>
            <div className="w-get" id="h3-get">
              <StoreBadges qr onDark tag="w-hero" />
            </div>
            <UploadBlock skin="dark" label={UPLOAD_HERE_LABEL} />
          </div>
          <PhoneVideo eager src="/home/stop-cutting.mp4" poster="/home/stop-cutting.jpg" label="Stop cutting your videos: a 24-second CherryClipz spot" />
        </div>
      </section>

      <section id="w-phone" className="w-phone">
        <div className="wrap">
          <span className="eyebrow">{W.phoneEyebrow}</span>
          <h2 className="h2">{W.phoneTitle}</h2>
          <div className="w-three">
            {W.phoneSteps.map((s, k) => (
              <figure key={s.title}>
                {s.video ? <PhoneClip src={s.video} label={s.label} /> : <PhoneShot src={s.img || ""} alt={s.label} />}
                <figcaption>
                  <span className="n">{k + 1}</span>
                  <b>{s.title}</b>
                  <p>{s.body}</p>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section id="w-web" className="w-web">
        <div className="wrap">
          <div className="two">
            <div>
              <span className="eyebrow">{W.webEyebrow}</span>
              <h2 className="h2">{W.webTitle}</h2>
            </div>
            <p className="lede">{W.webBody}</p>
          </div>
          <div style={{ marginTop: "clamp(32px,4vw,52px)" }}>
            <ClipSheet />
          </div>
          <div className="h3-under">
            <span>
              {SHEET.real} {SHEET.try}
            </span>
            <a className="h3-real" href={REAL_EDITOR_URL} target="_blank" rel="noreferrer">
              {SHEET.openReal}
            </a>
          </div>
          <div className="w-upload">
            <div>
              <h3>{W.uploadTitle}</h3>
              <p>{W.uploadNote}</p>
              <p className="langs">{LANGS}</p>
            </div>
            <UploadCard />
          </div>
        </div>
      </section>

      <AiSection id="w-ai" />

      <section id="w-tour" className="w-tour">
        <div className="wrap">
          <span className="eyebrow">{W.tourEyebrow}</span>
          <h2 className="h2">{W.tourTitle}</h2>
          <div className="w-tour-list">
            <FeatureList ids={TOUR_IDS} preset={["supercut", "realpeople"]} tag="w-tour" />
          </div>
        </div>
      </section>

      <section id="w-pricing">
        <div className="wrap">
          <div className="price-head">
            <div>
              <span className="eyebrow">{PLANS.eyebrow}</span>
              <h2 className="h2">{PLANS.title}</h2>
            </div>
            <button type="button" className="link" onClick={act.pricing}>
              {PLANS.compare}
            </button>
          </div>
          <PlanCards plans={PLANS.items} perMonth={PLANS.perMonth} onPrimary={() => scrollToId("h3-upload")} onPlans={act.pricing} />
        </div>
      </section>

      <section className="w-close">
        <div className="wrap">
          <h2>
            {W.closeTitle1}
            <br />
            <span className="rw">{W.closeTitle2}</span>
          </h2>
          <StoreBadges qr onDark tag="w-close" />
          <button type="button" className="w-weblink" onClick={() => scrollToId("h3-upload")}>
            {W.closeWeb} <span aria-hidden>→</span>
          </button>
        </div>
      </section>

      <H3Footer />
      <GetAppBar text={BADGES.free} tag="w" />
    </div>
  );
};

const W_CSS = `
.h3-w .w-hero{padding-block:clamp(36px,5vw,72px) clamp(56px,7vw,96px);background:radial-gradient(70% 60% at 78% 35%,rgba(230,29,80,.2),transparent 62%)}
.h3-w .w-hero-grid{display:grid;grid-template-columns:minmax(0,1.3fr) minmax(0,.7fr);gap:clamp(30px,5vw,72px);align-items:center}
.h3-w h1{font-weight:800;font-size:clamp(46px,7vw,98px);line-height:.92;letter-spacing:-.05em;margin-top:22px}
.h3-w .w-sub{margin-top:24px;max-width:46ch}
.h3-w .w-get{margin-top:30px}
.h3-w .w-weblink{margin-top:22px;border:0;background:transparent;padding:0;font:500 15.5px/1.4 var(--fb);color:var(--ink2);text-decoration:underline;text-decoration-color:var(--line2);text-underline-offset:4px}
.h3-w .w-weblink:hover{color:var(--ink);text-decoration-color:var(--cherry)}
@media (max-width:900px){.h3-w .w-hero-grid{grid-template-columns:1fr}.h3-w .w-hero .h3-device{width:min(76vw,300px)}}

.h3-w .w-phone{border-top:1px solid var(--line)}
.h3-w .w-three{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:clamp(18px,3vw,40px);margin-top:clamp(32px,4vw,52px)}
.h3-w .w-three figure{margin:0;display:grid;gap:18px;justify-items:center}
.h3-w .w-three .h3-device{width:min(100%,280px)}
.h3-w .w-three figcaption{display:grid;grid-template-columns:30px 1fr;gap:4px 12px;max-width:300px}
.h3-w .w-three .n{grid-row:span 2;width:30px;height:30px;border-radius:50%;background:var(--cherry);color:#fff;display:grid;place-items:center;font:700 14px/1 var(--fd)}
.h3-w .w-three b{font:700 20px/1.2 var(--fd);letter-spacing:-.02em;padding-top:4px}
.h3-w .w-three p{font-size:15.5px;color:var(--ink2);line-height:1.45}
@media (max-width:760px){.h3-w .w-three{grid-template-columns:1fr;gap:40px}.h3-w .w-three .h3-device{width:min(70vw,270px)}}

.h3-w .w-web{background:var(--bg2);border-block:1px solid var(--line)}
.h3-w .w-upload{display:grid;grid-template-columns:minmax(0,.8fr) minmax(0,1.2fr);gap:clamp(20px,4vw,56px);align-items:center;margin-top:clamp(36px,4vw,56px)}
.h3-w .w-upload h3{font-size:clamp(28px,3vw,40px);font-weight:700;letter-spacing:-.03em}
.h3-w .w-upload p{color:var(--ink2);margin-top:10px;font-size:17px}
.h3-w .w-upload .langs{font:500 13px/1.5 var(--fm);color:var(--ink3);margin-top:18px}
@media (max-width:860px){.h3-w .w-upload{grid-template-columns:1fr}}

.h3-w .w-tour-list{max-width:880px;margin:clamp(28px,4vw,44px) auto 0}

.h3-w .w-close{text-align:center;background:radial-gradient(60% 70% at 50% 100%,rgba(230,29,80,.22),transparent 70%);border-top:1px solid var(--line)}
.h3-w .w-close .wrap{display:grid;justify-items:center;gap:26px}
.h3-w .w-close h2{font-weight:800;font-size:clamp(42px,6.6vw,92px);line-height:.95;letter-spacing:-.05em}
.h3-w .w-close .h3-stores{justify-content:center}
`;

export default LayoutWPage;
